import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { PageHeader } from "@/components/shared/DashKit";
import { QualityBadge } from "@/components/shared/Badges";
import { linkExample } from "@/lib/linkExample";
import { speedLabel, dropLabel } from "@/lib/labels";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { detectCustomComments } from "@/lib/customComments";

const ENTITIES = { "&nbsp;": " ", "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&apos;": "'" };
function cleanDescription(raw) {
  if (!raw) return "";
  let t = String(raw)
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(p|div|li)\s*>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z#0-9]+;/gi, (m) => ENTITIES[m.toLowerCase()] ?? " ")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/[ \t]{2,}/g, " ");
  t = t.split("\n").map((l) => l.trim()).join("\n").replace(/\n{3,}/g, "\n\n").trim();
  return t;
}

export default function NewOrder() {
  const nav = useNavigate();
  const { t } = useContent();
  const summaryRef = useRef(null);
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [balance, setBalance] = useState(0);
  const [sel, setSel] = useState({ platform: "", category: "", service: null });
  const [target, setTarget] = useState("");
  const [quantity, setQuantity] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)); api.get("/wallet").then((r) => setBalance(r.data.balance)); }, []);
  useEffect(() => {
    if (sel.platform) api.get(`/categories?platform_id=${sel.platform}`).then((r) => setCategories(r.data));
    else setCategories([]);
  }, [sel.platform]);
  useEffect(() => {
    if (sel.platform && sel.category) {
      const p = new URLSearchParams({ platform_id: sel.platform, category_id: sel.category, limit: "100" });
      api.get(`/services?${p}`).then((r) => setServices(r.data.items));
    } else setServices([]);
  }, [sel.platform, sel.category]);

  const svc = sel.service;
  const isCustomComments = detectCustomComments(svc);
  const commentLines = comments.split("\n").map((l) => l.trim()).filter(Boolean);
  const qty = isCustomComments ? commentLines.length : (Number(quantity) || 0);
  const price = svc ? (svc.price_per_1000 * qty) / 1000 : 0;
  const after = balance - price;
  const valid = svc && qty >= svc.min_quantity && qty <= svc.max_quantity && target.length > 2 && after >= 0;

  const place = async () => {
    setLoading(true);
    try {
      const payload = { service_id: svc.id, target, quantity: qty };
      if (isCustomComments) payload.comments = commentLines.join("\n");
      const { data } = await api.post("/orders", payload);
      toast.success(t("client.no.created", "Commande créée !"));
      nav(`/dashboard/orders/${data.id}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const cleanComments = () => {
    const seen = new Set(); const out = [];
    for (const l of comments.split("\n")) { const v = l.trim(); if (v && !seen.has(v)) { seen.add(v); out.push(v); } }
    setComments(out.join("\n"));
  };

  const handleCommentsFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const txt = String(reader.result || ""); setComments((p) => (p.trim() ? p.replace(/\n+$/, "") + "\n" : "") + txt); };
    reader.readAsText(file); e.target.value = "";
  };

  return (
    <div>
      <PageHeader title={t("client.no.title", "Nouvelle commande")} subtitle={t("client.no.subtitle", "Sélectionnez un service et lancez votre promotion en quelques secondes.")} />
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          {/* Cascade filters: Platform -> Category */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("client.no.platform", "Plateforme")}</label>
                <Select value={sel.platform} onValueChange={(v) => setSel({ platform: v, category: "", service: null })}>
                  <SelectTrigger data-testid="new-order-platform" className="w-full"><SelectValue placeholder={t("client.no.choose_platform", "Choisir une plateforme")} /></SelectTrigger>
                  <SelectContent>
                    {platforms.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`np-platform-${p.name}`}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("client.dash.category", "Catégorie")}</label>
                <Select value={sel.category} onValueChange={(v) => setSel((s) => ({ ...s, category: v, service: null }))} disabled={!sel.platform}>
                  <SelectTrigger data-testid="new-order-category" className="w-full"><SelectValue placeholder={t("client.no.choose_category", "Choisir une catégorie")} /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`np-cat-${c.name}`}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Services list */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display text-sm font-semibold">{t("client.dash.service", "Service")}</h3>
            {!sel.category ? (
              <div className="text-sm text-muted-foreground" data-testid="np-select-prompt">{t("client.no.select_platform_category", "Sélectionnez une plateforme et une catégorie pour afficher les services.")}</div>
            ) : services.length === 0 ? (
              <div className="text-sm text-muted-foreground">{t("client.no.no_services", "Aucun service pour cette sélection.")}</div>
            ) : (
              <div className="space-y-2">
                {services.map((s) => (
                  <button key={s.id} data-testid={`np-service-${s.id}`} onClick={() => { setSel((p) => ({ ...p, service: s })); setQuantity(s.min_quantity); setComments(""); if (window.innerWidth < 1024) setTimeout(() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }}
                    className={`flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${svc?.id === s.id ? "border-accent bg-accent/5" : "border-border hover:bg-secondary"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2 text-sm font-medium [overflow-wrap:anywhere]"><span className="min-w-0">{s.name}</span> {svc?.id === s.id && <Check className="h-4 w-4 shrink-0 text-accent" />}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{speedLabel(t, s.speed_description)} · {t("client.no.drop", "Chute")} {dropLabel(t, s.drop_level)} · {s.refill_available ? `${t("client.no.refill", "Recharge")} ${s.refill_days}${t("client.no.days", "j")}` : t("client.no.no_refill", "Sans recharge")}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <QualityBadge level={s.quality_level} />
                      <div className="mt-1 font-display text-sm font-bold"><Amount value={s.price_per_1000} /><span className="text-xs font-normal text-muted-foreground">/1k</span></div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary panel */}
        <div ref={summaryRef} data-testid="new-order-summary" className="scroll-mt-20 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-display font-semibold">{t("client.no.summary", "Résumé de la commande")}</h3>
            {!svc ? (
              <p className="mt-4 text-sm text-muted-foreground">{t("client.no.select_to_continue", "Sélectionnez un service pour continuer.")}</p>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-lg bg-secondary/40 p-3 text-sm">
                  <div className="font-medium">{svc.name}</div>
                  <QualityBadge level={svc.quality_level} />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("client.no.target", "Cible (URL / @nom)")}</label>
                  <input value={target} onChange={(e) => setTarget(e.target.value)} data-testid="new-order-target" placeholder={linkExample(svc?.name)}
                    className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
                </div>
                {isCustomComments ? (
                  <div>
                    <label className="text-sm font-medium">{t("client.no.comments_label", "Commentaires (1 par ligne)")} ({svc.min_quantity.toLocaleString()}–{svc.max_quantity.toLocaleString()})</label>
                    <textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="new-order-comments" rows={6}
                      placeholder={t("client.no.comments_placeholder", "Collez vos commentaires, un par ligne…")}
                      className="mt-1 w-full resize-y rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
                    <div data-testid="new-order-comments-counter" className={`mt-1 text-xs ${qty < svc.min_quantity || qty > svc.max_quantity ? "text-red-600" : "text-muted-foreground"}`}>
                      {qty.toLocaleString()} {t("client.no.comments_counter", "commentaire(s)")}
                    </div>
                    {comments.trim().length > 0 && (
                      <button type="button" data-testid="new-order-comments-clean" onClick={cleanComments}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                        {t("client.no.comments_clean", "Nettoyer (lignes vides + doublons)")}
                      </button>
                    )}
                    <label data-testid="new-order-comments-import" className="ml-2 mt-2 inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                      <input type="file" accept=".txt,text/plain" className="hidden" onChange={handleCommentsFile} />
                      {t("client.no.comments_import", "Importer un .txt")}
                    </label>
                    {comments.length > 0 && (qty < svc.min_quantity || qty > svc.max_quantity) && (
                      <div data-testid="new-order-comments-error" className="mt-1 text-xs text-red-600">
                        {t("client.no.comments_range", "Le nombre de commentaires doit être entre {min} et {max}.").replace("{min}", svc.min_quantity.toLocaleString()).replace("{max}", svc.max_quantity.toLocaleString())}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="text-sm font-medium">{t("client.dash.quantity", "Quantité")} ({svc.min_quantity.toLocaleString()}–{svc.max_quantity.toLocaleString()})</label>
                    <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="new-order-quantity"
                      className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
                  </div>
                )}
                <div className="space-y-1.5 rounded-lg border border-border p-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("client.no.price", "Prix")}</span><span className="font-display text-base font-bold"><Amount value={price} /></span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("client.no.current_balance", "Solde actuel")}</span><span><Amount value={balance} /></span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("client.no.after_order", "Après commande")}</span><span className={after < 0 ? "text-red-600" : ""}><Amount value={after} /></span></div>
                </div>
                {after < 0 && <div className="text-xs text-red-600">{t("client.no.insufficient", "Solde insuffisant.")}</div>}
                {isCustomComments && commentLines.length > 0 && (
                  <div data-testid="new-order-comments-recap" className="rounded-lg border border-border bg-secondary/30 p-3">
                    <div className="mb-1.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <span>{t("client.no.comments_recap", "Aperçu des commentaires")}</span>
                      <span>{commentLines.length}</span>
                    </div>
                    <ol className="max-h-40 list-decimal space-y-0.5 overflow-y-auto pl-5 text-sm text-muted-foreground">
                      {commentLines.map((c, i) => <li key={i} className="break-words">{c}</li>)}
                    </ol>
                  </div>
                )}
                <button disabled={!valid || loading} onClick={place} data-testid="new-order-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("client.no.place", "Passer la commande")}
                </button>

                {(cleanDescription(svc.description) || cleanDescription(svc.provider_instructions)) && (
                  <div data-testid="service-details" className="rounded-lg border border-border bg-secondary/30 p-4">
                    <h4 className="text-sm font-semibold">{t("client.no.details", "Détails du service")}</h4>
                    {cleanDescription(svc.description) && (
                      <p data-testid="service-details-text" className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                        {cleanDescription(svc.description)}
                      </p>
                    )}
                    {cleanDescription(svc.provider_instructions) && (
                      <div className="mt-3 border-t border-border pt-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("client.no.instructions", "Instructions")}</div>
                        <p data-testid="service-instructions-text" className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                          {cleanDescription(svc.provider_instructions)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
